/* eslint-disable max-lines */
import { CommonModule, NgIf } from '@angular/common';
import {
  AfterViewChecked,
  Component,
  effect,
  inject,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {
  CheckinForm,
  Dialog,
  MyTripAncillaryComponent,
  MyTripDetailComponent,
  MyTripSearchComponent,
  TripsRequest
} from '@com-delta-idp/mnx-capability-trip-management-ui';
import {
  AnalyticsDataPopulateService,
  MyTripAem
} from '@com-delta-idp/mnx-sitewide-shared-core-smart';
import {
  CheckInNavigationService,
  PopUpModal
} from '@com-delta-idp/mnx-sitewide-shared-utils';
import { MessageBanner } from '@com-delta-omnichannel/mach-core-js';
import { MyTripAdapterService } from '../data-access/adapters/my-trip-adapter.service';
import { modalContent } from '../data-access/adapters/search.adapter';
import { MyTripFacade } from '../data-access/facades/my-trip.facade';
import { MyTripFacadeHelper } from '../data-access/facades/my-trip.facade.helper';
import { SearchFacade } from '../data-access/facades/search.facade';
import { MY_TRIPS_LOADER_CONTENT } from '../data-access/models/enums/my-trip.enums';
import { CheckInService } from '../data-access/services/checkin.service';
import { MyTripFormService } from '../data-access/services/my-trip-form.service';
import { ViewTripDetailsService } from '../data-access/services/view-trip-details.service';
import { getFormErrorCount } from '../data-access/utils/mytrip-form.utils';
import { GenericFormModule } from './generic-form/generic-form.module';

@Component({
  selector: 'idp-capability-my-trips-container',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [
    NgIf,
    CommonModule,
    MyTripSearchComponent,
    MyTripDetailComponent,
    MyTripAncillaryComponent,
    GenericFormModule
  ],
  templateUrl: './my-trips.component.html',
  styleUrls: ['./my-trips.component.scss']
})
export class MyTripsContainerComponent
  implements OnInit, AfterViewChecked, OnChanges
{
  @Input() content: MyTripAem | undefined;
  @Input() isSelected = false;

  @ViewChild('myTripSearch')
  myTripSearch!: MyTripSearchComponent;

  messageBanner!: MessageBanner;

  dialogContent = {} as Dialog;
  showDialog = false;
  private dialogNeedsOpening = false;
  loaderContent = {
    label: MY_TRIPS_LOADER_CONTENT
  };

  facade = inject(MyTripFacade);
  formService = inject(MyTripFormService);
  viewTripService = inject(ViewTripDetailsService);
  checkInService = inject(CheckInService);
  facadeHelper = inject(MyTripFacadeHelper);
  searchFacade = inject(SearchFacade);
  checkInNavigationService = inject(CheckInNavigationService);
  adapter = inject(MyTripAdapterService);
  analyticsService = inject(AnalyticsDataPopulateService);

  private _ = effect(() => {
    const showError = this.facade.displayErrorMessage();
    if (showError) {
      setTimeout(() => {
        this.adapter.errorBannerMessage =
          this.formService.getGenericErrorMessage(
            this.facade.myTripStaticContent()
          );
        this.messageBanner = new MessageBanner(this.adapter.setErrorBanner());
      }, 100);
    }
  });

  ngOnInit(): void {
    this.facade.init(this.content);
  }

  ngAfterViewChecked(): void {
    if (this.dialogNeedsOpening) {
      const dialog = document.getElementById('help_popup') as HTMLDialogElement;
      if (dialog) {
        dialog.showModal();
        this.dialogNeedsOpening = false;
      }
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['isSelected']?.currentValue) {
      const currentContent = this.facadeHelper.searchContent();
      if (currentContent?.formGroup) {
        currentContent.formGroup.setErrors(null);
        currentContent.submitClicked = false;
        this.facadeHelper.searchContent.set({ ...currentContent });
      }
    }
  }

  onFindTripByChange(currentType: string): void {
    this.facadeHelper.updateCurrentType(currentType);
    if (currentType) {
      this.formService.onFindTripByChange(
        currentType,
        this.facadeHelper.searchContent().formGroup
      );
    }
  }

  onFormSubmit(): void {
    const searchContent = this.facadeHelper.searchContent();
    searchContent.submitClicked = true;
    searchContent.errorCount = getFormErrorCount(searchContent.formGroup);
    if (searchContent.errorCount === 0) {
      this.facade.spinner.set(true);
      this.formService.onSubmitActionForm(searchContent);
    }
  }

  tripDetailsRedirect(request: TripsRequest): void {
    this.facade.spinner.set(true);
    this.searchFacade.findTrip(request);
  }

  checkInRedirect(request: CheckinForm): void {
    this.facade.spinner.set(true);
    this.checkInNavigationService.navigateToCheckInFindPNR(
      request,
      this.checkInService.checkinPath
    );
  }

  openDialog(popup: PopUpModal): void {
    this.dialogContent = modalContent(popup);
    this.showDialog = true;
    this.dialogNeedsOpening = true;
  }

  closeDialog(): void {
    const dialog = document.getElementById('help_popup') as HTMLDialogElement;
    dialog?.close();
    this.showDialog = false;
  }

  handleAnalytics(element: HTMLElement): void {
    this.analyticsService.populateAnalyticsData(element);
  }
}
